import React, { useEffect, useState } from "react";
import { api, assetUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Pin, EyeOff, ArrowUp, ArrowDown, Search, Building2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import CitySelect from "@/components/CitySelect";

/**
 * AdminPopularStoresPage — 03/08.
 *
 * Admin curation surface for the homepage "Popular Stores" section.
 * Pick a city, then pin / hide / reorder the vendors that appear in
 * that city's public Popular Stores feed. Non-pinned vendors keep
 * their organic order (by live-campaign count).
 */
export default function AdminPopularStoresPage() {
  const [city, setCity] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async (targetCity) => {
    if (!targetCity) { setRows([]); return; }
    setLoading(true);
    try {
      const r = await api.adminPopularStores(targetCity);
      setRows(r.data?.stores || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load stores for this city");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (city) load(city); }, [city]);

  const update = async (vendor_id, patch) => {
    setBusyId(vendor_id);
    try {
      await api.adminPinPopularStore({ vendor_id, city, ...patch });
      // Optimistic local update
      setRows((arr) => arr.map((r) => (r.vendor_id === vendor_id ? { ...r, ...patch } : r)));
      // Refresh sort order from server so the visible order matches the public feed
      await load(city);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const move = async (vendor_id, delta) => {
    const pinnedRows = rows.filter((r) => r.pinned && !r.hidden);
    const idx = pinnedRows.findIndex((r) => r.vendor_id === vendor_id);
    if (idx < 0) return;
    const newIdx = Math.max(0, Math.min(pinnedRows.length - 1, idx + delta));
    if (newIdx === idx) return;
    // Renumber sort_order for the reordered pinned list
    const reordered = [...pinnedRows];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    setBusyId(vendor_id);
    try {
      await Promise.all(
        reordered.map((r, i) =>
          api.adminPinPopularStore({ vendor_id: r.vendor_id, city, sort_order: i + 1 }),
        ),
      );
      await load(city);
    } catch (e) {
      toast.error("Reorder failed");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = rows.filter((r) =>
    !q || (r.business || "").toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <div className="space-y-6" data-testid="admin-popular-stores-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold">Popular Stores</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Curate the homepage &quot;Businesses everyone&apos;s rewarding&quot; section per city.
            Pinned stores lead the list; hidden stores never appear.
          </p>
        </div>
        {city && (
          <Button variant="outline" onClick={() => load(city)} data-testid="ps-refresh">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        )}
      </div>

      <Card className="p-4 md:p-5 space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              City
            </label>
            <div className="mt-1">
              <CitySelect
                value={city}
                onChange={setCity}
                placeholder="Pick a city to curate"
                data-testid="ps-city-select"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Search vendors in this city
            </label>
            <div className="relative mt-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by business name"
                className="pl-9"
                disabled={!city}
                data-testid="ps-search-input"
              />
            </div>
          </div>
        </div>
      </Card>

      {!city ? (
        <Card className="p-10 text-center text-muted-foreground">
          <Building2 className="h-8 w-8 mx-auto mb-3 opacity-40" />
          Pick a city above to load its stores.
        </Card>
      ) : loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">No vendors match this filter.</Card>
      ) : (
        <div className="space-y-2" data-testid="ps-rows">
          {filtered.map((r) => (
            <Card
              key={r.vendor_id}
              className={`p-3 md:p-4 flex items-center gap-3 md:gap-4 transition-colors ${r.hidden ? "opacity-50" : ""} ${r.pinned ? "border-[#F5A800] border-2" : ""}`}
              data-testid={`ps-row-${r.vendor_id}`}
            >
              <div className="h-11 w-11 rounded-lg bg-[#0A0A0A] text-[#F5A800] flex items-center justify-center border-2 border-[#0A0A0A] overflow-hidden shrink-0">
                {r.logo_url ? (
                  <img
                    src={assetUrl(r.logo_url)}
                    alt=""
                    className="w-full h-full object-contain bg-white"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <span className="font-heading text-lg">{(r.business || "?").slice(0, 1).toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{r.business}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {r.business_type || "—"} · {r.count} live offer{r.count === 1 ? "" : "s"}
                  {r.pinned && <span className="ml-2 text-[#F5A800] font-bold">📌 pinned #{r.sort_order || "?"}</span>}
                  {r.hidden && <span className="ml-2 text-destructive font-bold">hidden</span>}
                </p>
              </div>
              {r.pinned && !r.hidden && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => move(r.vendor_id, -1)}
                    disabled={busyId === r.vendor_id}
                    data-testid={`ps-up-${r.vendor_id}`}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => move(r.vendor_id, 1)}
                    disabled={busyId === r.vendor_id}
                    data-testid={`ps-down-${r.vendor_id}`}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                  <Pin className="h-3.5 w-3.5 text-[#F5A800]" />
                  <span className="uppercase tracking-wider text-muted-foreground">Pin</span>
                  <Switch
                    checked={!!r.pinned}
                    onCheckedChange={(v) => update(r.vendor_id, { pinned: !!v, hidden: v ? false : r.hidden, sort_order: v ? (rows.filter(x => x.pinned).length + 1) : undefined })}
                    disabled={busyId === r.vendor_id}
                    data-testid={`ps-pin-${r.vendor_id}`}
                  />
                </label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                  <EyeOff className="h-3.5 w-3.5 text-destructive" />
                  <span className="uppercase tracking-wider text-muted-foreground">Hide</span>
                  <Switch
                    checked={!!r.hidden}
                    onCheckedChange={(v) => update(r.vendor_id, { hidden: !!v, pinned: v ? false : r.pinned })}
                    disabled={busyId === r.vendor_id}
                    data-testid={`ps-hide-${r.vendor_id}`}
                  />
                </label>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
